import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
  Server,
  Wifi,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  BarChart3,
  Globe,
  Users,
  Zap,
  Search,
  Filter,
  X
} from 'lucide-react';

interface ServiceHealth {
  name: string;
  status: 'up' | 'down' | 'degraded';
  response_time_ms: number | null;
  error: string | null;
}

interface SystemStatus {
  status: 'up' | 'down' | 'degraded' | 'unknown';
  last_check: string | null;
  response_time_ms: number | null;
  open_incidents: number;
  services: ServiceHealth[];
}

interface UptimeStats {
  period_start: string;
  period_end: string;
  total_checks: number;
  successful_checks: number;
  failed_checks: number;
  uptime_percentage: number;
  average_response_time_ms: number | null;
  incidents_count: number;
  total_downtime_minutes: number;
}

interface Incident {
  id: string;
  title: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'investigating' | 'identified' | 'monitoring' | 'resolved';
  started_at: string;
  resolved_at: string | null;
  affected_services: string[];
}

interface APIStats {
  period_hours: number;
  total_requests: number;
  error_requests: number;
  success_requests: number;
  error_rate_percentage: number;
  response_time_stats: {
    avg_response_time: number;
    max_response_time: number;
    min_response_time: number;
  };
  top_endpoints: Array<{
    _id: { method: string; path: string };
    count: number;
    avg_response_time: number;
    error_count: number;
  }>;
  hourly_breakdown: Array<{
    _id: { hour: string };
    total_requests: number;
    error_requests: number;
    avg_response_time: number;
  }>;
}

interface APIAlert {
  id: string;
  timestamp: string;
  alert_type: string;
  endpoint?: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  threshold_value?: number;
  actual_value?: number;
  resolved: boolean;
  resolved_at?: string;
}

interface EndpointMetric {
  endpoint: string;
  method: string;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  avg_response_time_ms: number;
  error_rate: number;
}

const MonitoringPage: React.FC = () => {
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [uptimeStats, setUptimeStats] = useState<UptimeStats | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState(24);
  
  // API Logs state
  const [apiStats, setApiStats] = useState<APIStats | null>(null);
  const [apiAlerts, setApiAlerts] = useState<APIAlert[]>([]);
  const [endpointMetrics, setEndpointMetrics] = useState<EndpointMetric[]>([]);
  const [apiLogsLoading, setApiLogsLoading] = useState(false);
  
  // Filtering state
  const [emailFilter, setEmailFilter] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');
  const [filteredLogs, setFilteredLogs] = useState<any[]>([]);
  const [userActivity, setUserActivity] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchSystemStatus = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/monitoring/status`);
      if (!response.ok) throw new Error('Failed to fetch system status');
      const data = await response.json();
      setSystemStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const fetchUptimeStats = async (hours: number) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/monitoring/uptime-stats?hours=${hours}`);
      if (!response.ok) throw new Error('Failed to fetch uptime stats');
      const data = await response.json();
      setUptimeStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const fetchIncidents = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/monitoring/incidents?limit=10`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch incidents');
      const data = await response.json();
      setIncidents(data);
    } catch (err) {
      console.error('Failed to fetch incidents:', err);
      // Don't set error for incidents as it might be a permission issue
    }
  };

  // API Logs fetch functions
  const fetchApiStats = async (hours: number) => {
    try {
      setApiLogsLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/api-logs/stats?hours=${hours}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch API stats');
      const data = await response.json();
      setApiStats(data);
    } catch (err) {
      console.error('Failed to fetch API stats:', err);
    } finally {
      setApiLogsLoading(false);
    }
  };

  const fetchApiAlerts = async (hours: number) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/api-logs/alerts?hours=${hours}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch API alerts');
      const data = await response.json();
      setApiAlerts(data);
    } catch (err) {
      console.error('Failed to fetch API alerts:', err);
    }
  };

  const fetchEndpointMetrics = async (hours: number) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/api-logs/endpoint-performance?hours=${hours}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch endpoint metrics');
      const data = await response.json();
      setEndpointMetrics(data);
    } catch (err) {
      console.error('Failed to fetch endpoint metrics:', err);
    }
  };

  // New filtering functions
  const fetchFilteredLogs = async (hours: number, email?: string, username?: string) => {
    try {
      setApiLogsLoading(true);
      const params = new URLSearchParams({
        hours: hours.toString(),
        limit: '100'
      });
      
      if (email) params.append('email', email);
      if (username) params.append('username', username);
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/api-logs/filtered-logs?${params}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch filtered logs');
      const data = await response.json();
      setFilteredLogs(data);
    } catch (err) {
      console.error('Failed to fetch filtered logs:', err);
    } finally {
      setApiLogsLoading(false);
    }
  };

  const fetchUserActivity = async (hours: number, email?: string, username?: string) => {
    try {
      const params = new URLSearchParams({
        hours: hours.toString()
      });
      
      if (email) params.append('email', email);
      if (username) params.append('username', username);
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/api-logs/user-activity?${params}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch user activity');
      const data = await response.json();
      setUserActivity(data);
    } catch (err) {
      console.error('Failed to fetch user activity:', err);
    }
  };

  const searchLogs = async (query: string, hours: number, email?: string, username?: string) => {
    try {
      setApiLogsLoading(true);
      const params = new URLSearchParams({
        query,
        hours: hours.toString(),
        limit: '50'
      });
      
      if (email) params.append('email', email);
      if (username) params.append('username', username);
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/api-logs/search-logs?${params}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to search logs');
      const data = await response.json();
      setSearchResults(data);
    } catch (err) {
      console.error('Failed to search logs:', err);
    } finally {
      setApiLogsLoading(false);
    }
  };

  const handleApplyFilters = () => {
    if (emailFilter || usernameFilter) {
      fetchFilteredLogs(selectedPeriod, emailFilter, usernameFilter);
      fetchUserActivity(selectedPeriod, emailFilter, usernameFilter);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      searchLogs(searchQuery, selectedPeriod, emailFilter, usernameFilter);
    }
  };

  const clearFilters = () => {
    setEmailFilter('');
    setUsernameFilter('');
    setSearchQuery('');
    setFilteredLogs([]);
    setUserActivity(null);
    setSearchResults([]);
  };

  const performHealthCheck = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/monitoring/health-check`);
      if (!response.ok) throw new Error('Failed to perform health check');
      await fetchSystemStatus();
      await fetchUptimeStats(selectedPeriod);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSystemStatus(),
        fetchUptimeStats(selectedPeriod),
        fetchIncidents(),
        fetchApiStats(selectedPeriod),
        fetchApiAlerts(selectedPeriod),
        fetchEndpointMetrics(selectedPeriod)
      ]);
      setLoading(false);
    };

    loadData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchSystemStatus();
      fetchUptimeStats(selectedPeriod);
      // API logs refresh less frequently to avoid overwhelming the system
      if (Math.random() < 0.3) { // 30% chance to refresh API logs
        fetchApiStats(selectedPeriod);
        fetchApiAlerts(selectedPeriod);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedPeriod]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'up': return 'text-green-600 bg-green-100';
      case 'down': return 'text-red-600 bg-red-100';
      case 'degraded': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'up': return <CheckCircle className="h-4 w-4" />;
      case 'down': return <AlertCircle className="h-4 w-4" />;
      case 'degraded': return <AlertTriangle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getServiceIcon = (serviceName: string) => {
    switch (serviceName.toLowerCase()) {
      case 'api': return <Server className="h-4 w-4" />;
      case 'mongodb': return <Database className="h-4 w-4" />;
      case 'redis': return <Wifi className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const formatResponseTime = (ms: number | null) => {
    if (ms === null) return 'N/A';
    return `${Math.round(ms)}ms`;
  };

  const formatUptime = (percentage: number) => {
    return `${percentage.toFixed(2)}%`;
  };

  if (loading && !systemStatus) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading monitoring data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">System Monitoring</h1>
        <Button onClick={performHealthCheck} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* System Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Status</CardTitle>
            {systemStatus && getStatusIcon(systemStatus.status)}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <Badge className={getStatusColor(systemStatus?.status || 'unknown')}>
                {systemStatus?.status?.toUpperCase() || 'UNKNOWN'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Last check: {systemStatus?.last_check ? 
                new Date(systemStatus.last_check).toLocaleTimeString() : 'Never'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatResponseTime(systemStatus?.response_time_ms || null)}
            </div>
            <p className="text-xs text-muted-foreground">
              Average: {formatResponseTime(uptimeStats?.average_response_time_ms || null)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uptime</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {uptimeStats ? formatUptime(uptimeStats.uptime_percentage) : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Last {selectedPeriod}h ({uptimeStats?.total_checks || 0} checks)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Incidents</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {systemStatus?.open_incidents || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Total incidents: {uptimeStats?.incidents_count || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="services" className="space-y-4">
        <TabsList>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="stats">Statistics</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="api-logs">API Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Service Health</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {systemStatus?.services?.map((service) => (
                  <div key={service.name} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getServiceIcon(service.name)}
                      <div>
                        <h3 className="font-medium capitalize">{service.name}</h3>
                        {service.error && (
                          <p className="text-sm text-red-600">{service.error}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-muted-foreground">
                        {formatResponseTime(service.response_time_ms)}
                      </span>
                      <Badge className={getStatusColor(service.status)}>
                        {service.status.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats" className="space-y-4">
          <div className="flex space-x-2 mb-4">
            {[1, 6, 24, 168].map((hours) => (
              <Button
                key={hours}
                variant={selectedPeriod === hours ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod(hours)}
              >
                {hours === 1 ? '1h' : hours === 6 ? '6h' : hours === 24 ? '24h' : '7d'}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Uptime Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {uptimeStats && (
                  <>
                    <div className="flex justify-between">
                      <span>Total Checks:</span>
                      <span className="font-medium">{uptimeStats.total_checks}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Successful:</span>
                      <span className="font-medium text-green-600">{uptimeStats.successful_checks}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Failed:</span>
                      <span className="font-medium text-red-600">{uptimeStats.failed_checks}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Uptime:</span>
                      <span className="font-medium">{formatUptime(uptimeStats.uptime_percentage)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Avg Response Time:</span>
                      <span className="font-medium">{formatResponseTime(uptimeStats.average_response_time_ms)}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Downtime Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {uptimeStats && (
                  <>
                    <div className="flex justify-between">
                      <span>Total Downtime:</span>
                      <span className="font-medium">{Math.round(uptimeStats.total_downtime_minutes)} minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Incidents:</span>
                      <span className="font-medium">{uptimeStats.incidents_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Period:</span>
                      <span className="font-medium">
                        {new Date(uptimeStats.period_start).toLocaleDateString()} - {new Date(uptimeStats.period_end).toLocaleDateString()}
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="incidents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Incidents</CardTitle>
            </CardHeader>
            <CardContent>
              {incidents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No incidents found</p>
              ) : (
                <div className="space-y-4">
                  {incidents.map((incident) => (
                    <div key={incident.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium">{incident.title}</h3>
                        <div className="flex space-x-2">
                          <Badge variant={incident.severity === 'critical' ? 'destructive' : 'secondary'}>
                            {incident.severity}
                          </Badge>
                          <Badge variant={incident.status === 'resolved' ? 'default' : 'destructive'}>
                            {incident.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>Started: {new Date(incident.started_at).toLocaleString()}</p>
                        {incident.resolved_at && (
                          <p>Resolved: {new Date(incident.resolved_at).toLocaleString()}</p>
                        )}
                        {incident.affected_services.length > 0 && (
                          <p>Affected services: {incident.affected_services.join(', ')}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api-logs" className="space-y-4">
          {/* Filtering Controls */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filter & Search API Logs
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
              </div>
            </CardHeader>
            {showFilters && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email-filter">Filter by Email</Label>
                    <Input
                      id="email-filter"
                      placeholder="Enter email to filter..."
                      value={emailFilter}
                      onChange={(e) => setEmailFilter(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="username-filter">Filter by Username</Label>
                    <Input
                      id="username-filter"
                      placeholder="Enter username to filter..."
                      value={usernameFilter}
                      onChange={(e) => setUsernameFilter(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="search-query">Search Logs</Label>
                    <div className="flex gap-2">
                      <Input
                        id="search-query"
                        placeholder="Search in logs..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      <Button onClick={handleSearch} size="sm">
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleApplyFilters} disabled={apiLogsLoading}>
                    Apply Filters
                  </Button>
                  <Button variant="outline" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-2" />
                    Clear All
                  </Button>
                </div>
                
                {/* Filter Results Summary */}
                {(filteredLogs.length > 0 || userActivity || searchResults.length > 0) && (
                  <div className="mt-4 p-4 bg-muted rounded-lg">
                    <h4 className="font-medium mb-2">Filter Results:</h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      {filteredLogs.length > 0 && (
                        <p>• Found {filteredLogs.length} filtered log entries</p>
                      )}
                      {userActivity && (
                        <p>• {userActivity.unique_users} unique users in activity data</p>
                      )}
                      {searchResults.length > 0 && (
                        <p>• {searchResults.length} search results for "{searchQuery}"</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
                <Globe className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {apiStats?.total_requests?.toLocaleString() || 'N/A'}
                </div>
                <p className="text-xs text-muted-foreground">
                  Last {selectedPeriod}h
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {apiStats?.error_rate_percentage?.toFixed(2) || '0'}%
                </div>
                <p className="text-xs text-muted-foreground">
                  {apiStats?.error_requests || 0} errors
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {apiStats?.response_time_stats?.avg_response_time?.toFixed(0) || '0'}ms
                </div>
                <p className="text-xs text-muted-foreground">
                  Max: {apiStats?.response_time_stats?.max_response_time?.toFixed(0) || '0'}ms
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Alerts</CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {apiAlerts?.filter(alert => !alert.resolved).length || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  {apiAlerts?.length || 0} total alerts
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Top Endpoints
                </CardTitle>
              </CardHeader>
              <CardContent>
                {apiLogsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : apiStats?.top_endpoints?.length ? (
                  <div className="space-y-3">
                    {apiStats.top_endpoints.slice(0, 5).map((endpoint, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded">
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            <Badge variant="outline" className="mr-2">
                              {endpoint._id.method}
                            </Badge>
                            {endpoint._id.path}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {endpoint.count} requests • {endpoint.avg_response_time.toFixed(0)}ms avg
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{endpoint.count}</div>
                          {endpoint.error_count > 0 && (
                            <div className="text-xs text-red-500">{endpoint.error_count} errors</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No endpoint data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Recent API Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {apiAlerts?.length ? (
                  <div className="space-y-3">
                    {apiAlerts.slice(0, 5).map((alert) => (
                      <div key={alert.id} className="border rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <Badge
                            variant={alert.severity === 'critical' ? 'destructive' :
                                   alert.severity === 'error' ? 'destructive' :
                                   alert.severity === 'warning' ? 'default' : 'secondary'}
                          >
                            {alert.severity.toUpperCase()}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(alert.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-sm">{alert.message}</p>
                        {alert.endpoint && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Endpoint: {alert.endpoint}
                          </p>
                        )}
                        {alert.resolved && (
                          <Badge variant="outline" className="mt-2">
                            Resolved
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No alerts found</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Endpoint Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {endpointMetrics?.length ? (
                <div className="space-y-2">
                  {endpointMetrics.slice(0, 10).map((metric, index) => (
                    <div key={index} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          <Badge variant="outline" className="mr-2">
                            {metric.method}
                          </Badge>
                          {metric.endpoint}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {metric.total_requests} requests • {metric.avg_response_time_ms.toFixed(0)}ms avg
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm">
                          {((metric.successful_requests / metric.total_requests) * 100).toFixed(1)}% success
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {metric.error_rate.toFixed(2)}% error rate
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No performance data available</p>
              )}
            </CardContent>
          </Card>

          {/* Filtered Logs Results */}
          {filteredLogs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Filtered Log Entries ({filteredLogs.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredLogs.slice(0, 20).map((log, index) => (
                    <div key={index} className="border rounded p-3 text-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={log.status_code >= 400 ? 'destructive' : 'default'}>
                            {log.method} {log.status_code}
                          </Badge>
                          <span className="font-medium">{log.path}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {log.user && (
                        <div className="text-xs text-muted-foreground mb-1">
                          User: {log.user.email} ({log.user.name || 'Unknown'})
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        Response time: {log.response_time_ms}ms • IP: {log.client_ip}
                      </div>
                      {log.error && (
                        <div className="text-xs text-red-600 mt-1">
                          Error: {log.error}
                        </div>
                      )}
                    </div>
                  ))}
                  {filteredLogs.length > 20 && (
                    <p className="text-center text-sm text-muted-foreground">
                      Showing first 20 of {filteredLogs.length} results
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* User Activity Results */}
          {userActivity && userActivity.user_activities && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  User Activity Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 border rounded">
                    <div className="text-2xl font-bold">{userActivity.unique_users}</div>
                    <div className="text-sm text-muted-foreground">Unique Users</div>
                  </div>
                  <div className="text-center p-3 border rounded">
                    <div className="text-2xl font-bold">{userActivity.period_hours}h</div>
                    <div className="text-sm text-muted-foreground">Time Period</div>
                  </div>
                  <div className="text-center p-3 border rounded">
                    <div className="text-2xl font-bold">{userActivity.user_activities.length}</div>
                    <div className="text-sm text-muted-foreground">Active Users</div>
                  </div>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {userActivity.user_activities.slice(0, 10).map((activity: any, index: number) => (
                    <div key={index} className="border rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-medium">{activity._id.user_email}</div>
                          <div className="text-sm text-muted-foreground">
                            {activity._id.user_name || 'Unknown'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{activity.total_requests} requests</div>
                          <div className="text-xs text-muted-foreground">
                            {activity.avg_response_time.toFixed(0)}ms avg
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className={activity.error_requests > 0 ? 'text-red-600' : 'text-green-600'}>
                          {activity.error_requests} errors
                        </span>
                        <span className="text-muted-foreground">
                          Last: {new Date(activity.last_activity).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Search Results for "{searchQuery}" ({searchResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {searchResults.map((result, index) => (
                    <div key={index} className="border rounded p-3 text-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={result.status_code >= 400 ? 'destructive' : 'default'}>
                            {result.method} {result.status_code}
                          </Badge>
                          <span className="font-medium">{result.path}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(result.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {result.user && (
                        <div className="text-xs text-muted-foreground mb-1">
                          User: {result.user.email} ({result.user.name || 'Unknown'})
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        Response time: {result.response_time_ms}ms • IP: {result.client_ip}
                      </div>
                      {result.user_agent && (
                        <div className="text-xs text-muted-foreground truncate">
                          User Agent: {result.user_agent}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-center">
            <Button
              onClick={() => {
                fetchApiStats(selectedPeriod);
                fetchApiAlerts(selectedPeriod);
                fetchEndpointMetrics(selectedPeriod);
              }}
              disabled={apiLogsLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${apiLogsLoading ? 'animate-spin' : ''}`} />
              Refresh API Logs
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MonitoringPage;